import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ResponsiveDataList } from '../../../core/components/ui/ResponsiveDataList';
import { formatDateShort } from '../../../core/components/ui/utils/formatters';
import { FAQCard } from './FAQCard';
import { FAQTableActions } from './FAQTableActions';

/**
 * FAQ Table Component
 *
 * Displays FAQ entries in a responsive table (desktop) or cards (mobile).
 * Uses ResponsiveDataList wrapper for TanStack Table with sorting and filtering.
 *
 * @param {Object} props
 * @param {Array} props.faqs - Array of FAQ objects
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 *
 * @example
 * <FAQTable
 *   faqs={faqs}
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 * />
 */
export function FAQTable({ faqs, onEdit, onDelete }) {
  const { t } = useTranslation();

  // Format creator name
  const formatCreator = (createdBy) => {
    if (!createdBy) return t('faq.unknownCreator', 'Unknown');
    if (createdBy.first_name && createdBy.last_name) {
      return `${createdBy.first_name} ${createdBy.last_name}`;
    }
    return createdBy.username || t('faq.unknownCreator', 'Unknown');
  };

  // Column definitions with translation functions
  const columns = useMemo(() => [
    {
      accessorKey: 'question',
      header: () => t('faq.question', 'Question'),
      cell: (info) => (
        <span className="line-clamp-2 text-default break-words">
          {info.getValue()}
        </span>
      ),
      enableSorting: true,
      enableGlobalFilter: true,
    },
    {
      accessorKey: 'answer',
      header: () => t('faq.answer', 'Answer'),
      cell: (info) => (
        <div className="max-w-md">
          <span className="text-subtle line-clamp-2 break-words text-sm block">
            {info.getValue()}
          </span>
        </div>
      ),
      enableSorting: false,
      enableGlobalFilter: true,
    },
    {
      accessorKey: 'created_by',
      header: () => t('faq.createdBy', 'Created By'),
      cell: (info) => (
        <span className="text-muted text-sm">
          {formatCreator(info.getValue())}
        </span>
      ),
      enableSorting: true,
      enableGlobalFilter: false,
    },
    {
      accessorKey: 'time_created',
      header: () => t('faq.createdAt', 'Created At'),
      cell: (info) => (
        <span className="text-subtle text-sm">
          {formatDateShort(info.getValue())}
        </span>
      ),
      enableSorting: true,
      enableGlobalFilter: false,
    },
    {
      id: 'actions',
      header: () => t('faq.actions', 'Actions'),
      cell: ({ row }) => (
        <FAQTableActions
          onEdit={() => onEdit(row.original)}
          onDelete={() => onDelete(row.original)}
        />
      ),
      enableSorting: false,
      enableGlobalFilter: false,
    },
  ], [onEdit, onDelete, t]);

  // Render mobile card
  const renderMobileCard = useCallback((row) => (
    <FAQCard
      faq={row.original}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  ), [onEdit, onDelete]);

  return (
    <ResponsiveDataList
      data={faqs}
      columns={columns}
      renderMobileItem={renderMobileCard}
      emptyMessage={t('faq.noFAQs', 'No FAQs found')}
      searchPlaceholder={t('faq.search', 'Search FAQs...')}
      breakpoint="md"
      enableSorting={true}
      enableFiltering={true}
      onRowClick={(faq) => onEdit(faq)}
    />
  );
}

FAQTable.propTypes = {
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
      time_created: PropTypes.string.isRequired,
      created_by: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        username: PropTypes.string,
      }),
    })
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
